import { getATLContextDomain, isFedRamp } from '@atlassian/atl-context';
import type { Environment } from './OauthServerDetails.types.tsx';

export const LOAD_START_MARK_KEY = 'PROJECT_CONFIGURE_MAIL_SERVER_MODAL_LOAD_START_MARK_KEY';

export const DELETE_SERVER_MODAL_SUCCESS_FLAG =
	'configure-mailserver-modal.ui.delete-mailserver-modal.flags.delete-success';

export const DELETE_SERVER_MODAL_FAILURE_FLAG =
	'configure-mailserver-modal.ui.delete-mailserver-modal.flags.delete-failure';

export const OAUTH__UPDATE_SERVER_MODAL_ERROR_FLAG =
	'configure-mailserver-modal.ui.update-oauth-mailserver-modal.flags.create-failure';

export const OAUTH_SAVE_AND_REAUTHORIZE_SERVER_MODAL_SUCCESS_FLAG =
	'configure-mailserver-modal.ui.update-oauth-save-and-reauthorize-mailserver-modal.flags.create-success';

export const OAUTH__SAVE_AND_REAUTHORIZE_SERVER_MODAL_ERROR_FLAG =
	'configure-mailserver-modal.ui.update-oauth-save-and-reauthorize-mailserver-modal.flags.create-failure';

export const CUSTOM_SERVER_MODAL_SUCCESS_FLAG =
	'configure-mailserver-modal.ui.basic-mailserver-modal.flags.create-success';

export const CUSTOM_SERVER_MODAL_ERROR_FLAG =
	'configure-mailserver-modal.ui.basic-mailserver-modal.flags.create-failure';

export const CUSTOM_SERVER_MODAL_INLINE_ERROR_MESSAGE =
	'configure-mail-server-modal.custom-mailserver-modal.inline-error';

export const ADD_MAIL_SERVER_MODAL = 'addMailServer';
export const EDIT_CUSTOM_MAIL_SERVER_MODAL = 'editCustomMailServer';
export const EDIT_OAUTH_MAIL_SERVER_MODAL = 'editOAUTHMailServer';
export const ADD_CUSTOM_MAIL_SERVER_MODAL = 'addCustomMailServer';
export const DELETE_MAIL_SERVER_MODAL = 'deleteMailServer';
export const SAVE_AND_REAUTHORIZE_MAIL_SERVER_MODAL = 'saveChanges';
export const BASIC_DETAILS_MAIL_SERVER_CANCEL_BTN = 'cancelSaveMailServerButton';
export const SAVE_AND_REAUTHORIZE_MAIL_SERVER_SUBMIT_BTN = 'saveMailServerButton';
export const SAVE_AND_REAUTHORIZE_MAIL_SERVER_CANCEL_BTN = 'cancelSaveMailServerButton';
export const DELETE_SERVER_SUBMIT_BTN = 'confirmDeleteMailServerButton';
export const DELETE_SERVER_CANCEL_BTN = 'cancelDeleteMailServerButton';
export const CUSTOM_DETAILS_MAIL_SERVER_SAVE_BTN = 'saveCustomMailServerButton';
export const CUSTOM_DETAILS_MAIL_SERVER_CANCEL_BTN = 'cancelSaveCustomMailServerButton';

export const GMAIL_SERVICE_KEY = 'jira-email-service-gmail';
export const MICROSOFT_SERVICE_KEY = 'jira-email-service-microsoft';
export const MICROSOFT_GCC_HIGH_SERVICE_KEY = 'jira-email-service-microsoft-gcc-high';

export const getOutboundAuthServiceUrl = (environment: Environment): string => {
	if (isFedRamp()) {
		return environment === 'prod'
			? 'https://outbound-auth.us-east-1.prod.atl-paas-us-gov-mod.net'
			: 'https://outbound-auth.us-east-1.staging.atl-paas-us-gov-mod.net';
	}
	return environment === 'prod'
		? 'https://outbound-auth.prod.atl-pass.net'
		: 'https://outbound-auth.staging.atl-pass.net';
};

// Values for the threeLOUrl key are taken from
// https://developer.atlassian.com/platform/outbound-auth/guides/environments/
// The above also explains why dev and staging use identical information.
// Values for serviceKey and containerId are taken from
// https://hello.atlassian.net/wiki/spaces/VPORT/pages/654247405/JSD+3LO+3rd+party+configuration+links+Google+Microsoft
export const getOAuth3LOProvider = () => {
	if (isFedRamp()) {
		return {
			[MICROSOFT_SERVICE_KEY]: {
				prod: {
					containerId: 'f09c06db-0957-4ede-8d6d-daceccc22995',
					threeLOUrl: `https://${getATLContextDomain('id', ['prod', 'fedramp-moderate'])}/outboundAuth/v2/start`,
				},
				staging: {
					containerId: 'aadbeed2-cccd-4596-a61d-f1f8e4924769',
					threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'fedramp-moderate'])}/outboundAuth/v2/start`,
				},
				dev: {
					containerId: 'aadbeed2-cccd-4596-a61d-f1f8e4924769',
					threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'fedramp-moderate'])}/outboundAuth/v2/start`,
				},
				local: {
					containerId: 'aadbeed2-cccd-4596-a61d-f1f8e4924769',
					threeLOUrl: `https://${getATLContextDomain('id', ['dev', 'fedramp-moderate'])}/outboundAuth/v2/start`,
				},
			},
			[GMAIL_SERVICE_KEY]: {
				prod: {
					containerId: 'f09c06db-0957-4ede-8d6d-daceccc22995',
					threeLOUrl: `https://${getATLContextDomain('id', ['prod', 'fedramp-moderate'])}/outboundAuth/v2/start`,
				},
				staging: {
					containerId: 'aadbeed2-cccd-4596-a61d-f1f8e4924769',
					threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'fedramp-moderate'])}/outboundAuth/v2/start`,
				},
				dev: {
					containerId: 'aadbeed2-cccd-4596-a61d-f1f8e4924769',
					threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'fedramp-moderate'])}/outboundAuth/v2/start`,
				},
				local: {
					containerId: 'aadbeed2-cccd-4596-a61d-f1f8e4924769',
					threeLOUrl: `https://${getATLContextDomain('id', ['dev', 'fedramp-moderate'])}/outboundAuth/v2/start`,
				},
			},
		} as const;
	}

	return {
		[MICROSOFT_SERVICE_KEY]: {
			prod: {
				containerId: '4236689d-d21d-4741-ab4f-71892b2d55f9',
				threeLOUrl: `https://${getATLContextDomain('id')}/outboundAuth/v2/start`,
			},
			staging: {
				containerId: 'e1b53a56-0a21-419e-a258-ee1d435ea0b8',
				threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'commercial'])}/outboundAuth/v2/start`,
			},
			dev: {
				containerId: 'e1b53a56-0a21-419e-a258-ee1d435ea0b8',
				threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'commercial'])}/outboundAuth/v2/start`,
			},
			local: {
				containerId: 'e1b53a56-0a21-419e-a258-ee1d435ea0b8',
				threeLOUrl: `https://${getATLContextDomain('id', ['dev', 'commercial'])}/outboundAuth/v2/start`,
			},
		},
		[GMAIL_SERVICE_KEY]: {
			prod: {
				containerId: 'c16a389e-0776-44b3-9528-19c82119a022',
				threeLOUrl: `https://${getATLContextDomain('id')}/outboundAuth/v2/start`,
			},
			staging: {
				containerId: 'f4b08e22-5fdf-43c0-b33b-5178bbae4caf',
				threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'commercial'])}/outboundAuth/v2/start`,
			},
			dev: {
				containerId: 'f4b08e22-5fdf-43c0-b33b-5178bbae4caf',
				threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'commercial'])}/outboundAuth/v2/start`,
			},
			local: {
				containerId: 'f4b08e22-5fdf-43c0-b33b-5178bbae4caf',
				threeLOUrl: `https://${getATLContextDomain('id', ['dev', 'commercial'])}/outboundAuth/v2/start`,
			},
		},
		// TODO: Update containerId to the correct value when available
		[MICROSOFT_GCC_HIGH_SERVICE_KEY]: {
			prod: {
				containerId: 'e1b53a56-0a21-419e-a258-ee1d435ea0b8',
				threeLOUrl: `https://${getATLContextDomain('id')}/outboundAuth/v2/start`,
			},
			staging: {
				containerId: 'e1b53a56-0a21-419e-a258-ee1d435ea0b8',
				threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'commercial'])}/outboundAuth/v2/start`,
			},
			dev: {
				containerId: 'e1b53a56-0a21-419e-a258-ee1d435ea0b8',
				threeLOUrl: `https://${getATLContextDomain('id', ['stg', 'commercial'])}/outboundAuth/v2/start`,
			},
			local: {
				containerId: 'e1b53a56-0a21-419e-a258-ee1d435ea0b8',
				threeLOUrl: `https://${getATLContextDomain('id', ['dev', 'commercial'])}/outboundAuth/v2/start`,
			},
		},
	} as const;
};
